import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError, dupDetail, euro } from "@/lib/api";
import { BarraElenco, Casella, esportaCsv, useSelezione } from "@/components/SelezioneFile";
import { SectionTitle, DuplicateWarning } from "@/components/common";
import { Modal, Row, Field, Select, TextArea } from "@/pages/Notizie";
import ComunicazioniModal from "@/components/ComunicazioniModal";
import GdprContactPanel, { useMarketingMap, MarketingBadge, ExportListaButton } from "@/components/GdprContactPanel";
import { useFormErrors, V, errSummary } from "@/lib/formErrors";
import { ReassignModal } from "@/pages/Acquirenti";
import { EntityDocs, ciBadge as _ciBadge } from "@/components/EntityDocs";
import { AppuntamentiScheda } from "@/components/AppuntamentiScheda";
import { useAuth } from "@/context/AuthContext";
import { useDupCheck } from "@/lib/useDupCheck";
import { TipoSoggetto, SocietaFields, NomeCard, isGiuridica, regoleGiuridica } from "@/components/SoggettoFields";
import { toast } from "sonner";
import { Plus, UserSquare, Lock, Phone, Mail, MessageSquare, UserCog, FileBadge, Building2, Link2, X, AlertTriangle, Pencil, FileText, ArrowUpDown, ShieldCheck, Archive, Send } from "lucide-react";
import { useSticky } from "@/lib/sticky";
import { postUpload } from "@/components/ConflittoNome";
import { SchedaHeader, SchedaTabs, Riquadro, AltriDati, AzioniRapide } from "@/components/SchedaCliente";


const SubTitle = ({ children }) => (
  <div className="text-[11px] font-bold uppercase tracking-wider text-[#FC82ED] mt-4 mb-2 first:mt-0">{children}</div>
);

export default function Proprietari() {
  const { monitoraggio: isDirezione, isSegreteria } = useAuth();
  const S = useSelezione();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useSticky("proprietari_q", "");
  const [soloDaCollegare, setSoloDaCollegare] = useState(false);
  const [sortBy, setSortBy] = useState("nome");
  const [soloMkt, setSoloMkt] = useState(false);
  const [modal, setModal] = useState(false);
  const [editRec, setEditRec] = useState(null);
  const [comm, setComm] = useState(null);
  const [azione, setAzione] = useState(null);
  const [reassign, setReassign] = useState(null);
  const [agents, setAgents] = useState([]);

  const mktMap = useMarketingMap("proprietario");
  const [archiviati, setArchiviati] = useState(false);
  const priv = isDirezione || isSegreteria;
  const load = () => api.get(archiviati ? "/proprietari?archiviati=true" : "/proprietari").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, [archiviati]); // eslint-disable-line
  const restore = async (id) => {
    try { await api.post(`/proprietari/${id}/ripristina`); toast.success("Venditore ripristinato."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { if (isDirezione) api.get("/agents?con_soci=true").then((r) => setAgents(r.data)); }, [isDirezione]);

  // Deep-link from an immobile: /proprietari?open=<id>
  useEffect(() => {
    const open = params.get("open");
    if (open) { setEditRec({ id: open, _scrollDocs: params.get("tab") === "docs", _scrollDati: params.get("tab") === "dati" }); setParams({}, { replace: true }); }
    // eslint-disable-next-line
  }, []);

  const sq = q.trim().toLowerCase();
  const filteredList = items.filter((p) => {
    if (soloDaCollegare && !p.da_collegare) return false;
    if (soloMkt && !(mktMap && mktMap[p.id])) return false;
    if (sq) {
      const hay = `${p.nome || ""} ${p.cognome || ""} ${p.telefono || ""} ${p.email || ""} ${p.codice_fiscale || ""}`.toLowerCase();
      if (!hay.includes(sq)) return false;
    }
    return true;
  });
  const sorted = [...filteredList].sort((a, b) => sortBy === "immobili"
    ? (b.immobili_count || 0) - (a.immobili_count || 0)
    : `${a.nome || ""} ${a.cognome || ""}`.trim().localeCompare(`${b.nome || ""} ${b.cognome || ""}`.trim()));

  return (
    <div className="fade-up">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <SectionTitle sub="Ogni Cliente Venditore deve essere collegato ad almeno un immobile che intende vendere.">Clienti Venditori</SectionTitle>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportListaButton contactType="proprietario" />
          <button data-testid="new-proprietario-btn" onClick={() => setModal(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm glow-pink transition-all"><Plus size={16}/> Nuovo Cliente Venditore</button>
        </div>
      </div>

      <div className="flex items-end gap-2 mb-5 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1">Cerca (nome, telefono, email, CF)</label>
          <input data-testid="vend-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente venditore…"
                 className="w-full px-3 py-2 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm" />
        </div>
        <button data-testid="filter-da-collegare" onClick={() => setSoloDaCollegare((v) => !v)}
                className={`px-3.5 py-2 rounded-lg text-xs font-semibold border transition-all flex items-center gap-1.5 ${soloDaCollegare ? "bg-[#FFB020] text-black border-[#FFB020]" : "border-[var(--border)] text-slate-600 hover:border-[#FFB020]/50"}`}>
          <AlertTriangle size={14}/> Solo da collegare
        </button>
        <button data-testid="filter-marketing-proprietari" onClick={() => setSoloMkt((v) => !v)}
                className={`px-3.5 py-2 rounded-lg text-xs font-semibold border transition-all flex items-center gap-1.5 ${soloMkt ? "bg-emerald-500 text-white border-emerald-500" : "border-[var(--border)] text-slate-600 hover:border-emerald-400/50"}`}>
          <ShieldCheck size={14}/> Solo consenso marketing
        </button>
        {priv && (
          <button data-testid="filter-archiviati" onClick={() => setArchiviati((v) => !v)}
                  className={`px-3.5 py-2 rounded-lg text-xs font-semibold border transition-all flex items-center gap-1.5 ${archiviati ? "bg-slate-700 text-white border-slate-700" : "border-[var(--border)] text-slate-600 hover:border-slate-400"}`}>
            <Archive size={14}/> {archiviati ? "Mostra attivi" : "Mostra archiviati"}
          </button>
        )}
        <div className="flex items-center gap-1.5">
          <ArrowUpDown size={14} className="text-[var(--muted)]" />
          <select data-testid="vend-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm">
            <option value="nome">Ordina per nome</option>
            <option value="immobili">Ordina per n° immobili</option>
          </select>
        </div>
      </div>

      {loading ? <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">{[...Array(3)].map((_,i)=><div key={i} className="skeleton h-48 rounded-2xl"/>)}</div> :
      items.length === 0 ? <div className="card-immo p-12 text-center"><UserSquare className="mx-auto text-[var(--muted)] mb-3" size={32}/><p className="text-[var(--muted)] text-sm">Nessun cliente venditore registrato.</p></div> :
      filteredList.length === 0 ? <div className="card-immo p-12 text-center"><UserSquare className="mx-auto text-[var(--muted)] mb-3" size={32}/><p className="text-[var(--muted)] text-sm">Nessun cliente corrisponde ai filtri.</p></div> :
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        <BarraElenco sel={S.sel} onAnnulla={S.annulla} onFatto={load}
          azioni={[]}
          onEsporta={(ids) => esportaCsv("Venditori", items.filter((x) => ids.includes(x.id)), [["nome", "Nome"], ["cognome", "Cognome"], ["telefono", "Telefono"], ["email", "Email"], ["owner_agent_name", "Agente"]])} />
        {sorted.length > 0 && <div className="md:col-span-2 xl:col-span-3 -mb-2"><button data-testid="venditori-seltutto" onClick={() => S.tutti(sorted.map((x) => x.id))} className="text-xs font-semibold text-slate-600 hover:text-[#111111]">Seleziona tutto ({sorted.length})</button></div>}
        {sorted.map((p) => (
          <div key={p.id} data-testid={`proprietario-${p.id}`} className={`relative card-immo p-5 pl-11 flex flex-col gap-3 ${S.sel.has(p.id) ? "ring-2 ring-[#FC82ED]" : ""}`}>
            <Casella on={S.sel.has(p.id)} onToggle={() => S.toggle(p.id)} testid={`proprietario-sel-${p.id}`} className="top-5 left-3" />
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FC82ED]/15 border border-[#FC82ED]/30 flex items-center justify-center text-[#FC82ED] font-bold text-sm shrink-0">{(p._masked ? "?" : p.nome?.slice(0,1)) || "?"}</div>
                <div>
                  <div className="font-semibold text-base">{p._masked ? (p.nome_display || "Contatto riservato") : <NomeCard c={p} />}</div>
                  {isDirezione && <div className="text-[11px] text-[var(--muted)]">Agente: {p.owner_agent_name}</div>}
                </div>
              </div>
              {p._masked && <Lock size={15} className="text-[var(--muted)]"/>}
            </div>

            {p._masked ? (
              <div className="text-xs text-[var(--muted)] flex items-center gap-1.5"><Lock size={12}/> Agente referente: <b className="text-slate-600">{p.agente_referente}</b></div>
            ) : (
              <div className="flex flex-col gap-1 text-xs text-slate-500">
                {p.telefono && <span className="flex items-center gap-1.5"><Phone size={12}/>{p.telefono}</span>}
                {p.email && <span className="flex items-center gap-1.5"><Mail size={12}/>{p.email}</span>}
                {p.codice_fiscale && <span className="flex items-center gap-1.5"><FileBadge size={12}/>{p.codice_fiscale}</span>}
              </div>
            )}

            {!p._masked && (
              <div className="flex items-center gap-2 flex-wrap">
                {p.da_collegare ? (
                  <span data-testid={`da-collegare-${p.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border text-[#FFB020] bg-[#FFB020]/10 border-[#FFB020]/30"><AlertTriangle size={12}/> Da collegare a un immobile</span>
                ) : (
                  <span data-testid={`immobili-count-${p.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border text-emerald-600 bg-emerald-400/10 border-emerald-400/30"><Building2 size={12}/> {p.immobili_count} immobil{p.immobili_count === 1 ? "e" : "i"} collegat{p.immobili_count === 1 ? "o" : "i"}</span>
                )}
                {p.ci_scadenza && _ciBadge(p.ci_scadenza)}
                <MarketingBadge map={mktMap} id={p.id} />
              </div>
            )}
            {p.archiviato && (
              <div className="flex items-center justify-between gap-2 rounded-lg bg-slate-100 border border-slate-200 px-2.5 py-1.5">
                <span data-testid={`archiviato-badge-${p.id}`} className="text-[11px] font-bold text-slate-600 inline-flex items-center gap-1"><Archive size={12}/> Archiviato</span>
                {priv && <button data-testid={`restore-${p.id}`} onClick={() => restore(p.id)} className="text-[11px] font-semibold text-[#FC82ED] hover:underline">Ripristina</button>}
              </div>
            )}

            {!p._masked && (
              <div className="mt-1 pt-3 border-t border-[var(--border)] flex items-center justify-between gap-2 flex-wrap">
                <button data-testid={`edit-prop-${p.id}`} onClick={() => setEditRec(p)} className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-[#FC82ED]"><Pencil size={14}/> Apri scheda</button>
                <div className="flex items-center gap-3">
                  <button data-testid={`comm-prop-${p.id}`} onClick={() => setComm(p)} className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-[#111111]"><MessageSquare size={14}/> Timeline</button>
                  {isDirezione && <button data-testid={`reassign-prop-${p.id}`} onClick={() => setReassign(p)} className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-[#FC82ED]"><UserCog size={14}/> Riassegna</button>}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>}

      {modal && <PropModal onClose={() => setModal(false)} onSaved={() => { setModal(false); load(); }} nav={nav} />}
      {editRec && <PropModal record={editRec} onClose={() => setEditRec(null)} onSaved={() => { setEditRec(null); load(); }} nav={nav} />}
      {comm && <ComunicazioniModal entityType="proprietario" entityId={comm.id} name={`${comm.nome} ${comm.cognome}`} phone={comm.telefono} onClose={() => setComm(null)} />}
      {reassign && <ReassignModal entityType="proprietario" record={reassign} agents={agents} onClose={() => setReassign(null)} onSaved={() => { setReassign(null); load(); }} />}
    </div>
  );
}

const etich = (x) => (x.tipo === "notizia" ? "Notizia" : "Incarico");
const opzioni = (allImmobili, allNotizie, esclusi, propId) => [
  ...allImmobili.map((im) => ({ ...im, tipo: "incarico" })),
  ...allNotizie.map((n) => ({ id: n.id, proprietario_id: n.proprietario_id, tipo: "notizia", titolo: [n.via_immobile || n.indirizzo, n.comune].filter(Boolean).join(", ") || "Notizia", zona: n.zona })),
].filter((x) => !esclusi.some((s) => s.id === x.id) && (!x.proprietario_id || x.proprietario_id === propId));
const Etichetta = ({ x }) => <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${x.tipo === "notizia" ? "bg-amber-100 text-amber-800" : "bg-[#FC82ED]/15 text-[#B8369F]"}`}>{etich(x)}</span>;

function NuovaNotiziaInline({ onCrea, testid }) {
  const [v, setV] = useState({ via: "", comune: "" });
  return (
    <div className="mt-2 flex items-end gap-2 flex-wrap" data-testid={testid}>
      <input data-testid={`${testid}-via`} value={v.via} onChange={(e) => setV({ ...v, via: e.target.value })} placeholder="Via e civico" className="flex-1 min-w-[140px] px-3 py-2.5 rounded-lg bg-white border border-[var(--border)] text-base sm:text-sm" />
      <input data-testid={`${testid}-comune`} value={v.comune} onChange={(e) => setV({ ...v, comune: e.target.value })} placeholder="Comune" className="w-36 px-3 py-2.5 rounded-lg bg-white border border-[var(--border)] text-base sm:text-sm" />
      <button data-testid={`${testid}-btn`} disabled={!v.via.trim() || !v.comune.trim()} onClick={() => { onCrea(v); setV({ via: "", comune: "" }); }} className="px-3.5 py-2.5 rounded-lg bg-white border border-[var(--border)] text-sm font-semibold disabled:opacity-40 flex items-center gap-1.5"><Plus size={15}/> Nuova notizia</button>
    </div>
  );
}

function LinkedImmobiliCreate({ allImmobili, allNotizie, selected, setSelected, nuova, setNuova, onCreateAbbinato }) {
  const [pick, setPick] = useState("");
  const available = opzioni(allImmobili, allNotizie, selected);
  const add = () => {
    const im = available.find((x) => x.id === pick);
    if (im) { setSelected([...selected, im]); setPick(""); }
  };
  return (
    <div className="rounded-xl border border-[#FC82ED]/30 bg-[#FC82ED]/[0.05] p-3">
      <div className="text-[11px] font-bold uppercase tracking-wide text-[#FC82ED] mb-2">Immobili collegati (facoltativo)</div>
      {selected.length === 0 && !nuova ? (
        <div className="text-xs text-[var(--muted)] mb-2">Puoi collegare un incarico o una notizia, crearne una al volo, oppure farlo più tardi.</div>
      ) : (
        <div className="flex flex-col gap-1.5 mb-2">
          {selected.map((im) => (
            <div key={im.id} data-testid={`sel-imm-${im.id}`} className="flex items-center justify-between gap-2 bg-white border border-[var(--border)] rounded-lg px-3 py-2 text-sm">
              <span className="flex items-center gap-2 truncate"><Etichetta x={im} /> {im.titolo || im.codice || "Immobile"}</span>
              <button data-testid={`unsel-imm-${im.id}`} onClick={() => setSelected(selected.filter((s) => s.id !== im.id))} className="text-slate-400 hover:text-red-500"><X size={15}/></button>
            </div>
          ))}
          {nuova && <div data-testid="sel-nuova-notizia" className="flex items-center justify-between gap-2 bg-white border border-[var(--border)] rounded-lg px-3 py-2 text-sm">
            <span className="flex items-center gap-2 truncate"><Etichetta x={{ tipo: "notizia" }} /> {nuova.via}, {nuova.comune} (nuova)</span>
            <button data-testid="unsel-nuova-notizia" onClick={() => setNuova(null)} className="text-slate-400 hover:text-red-500"><X size={15}/></button></div>}
        </div>
      )}
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1">Collega incarico o notizia</label>
          <select data-testid="link-existing-select" value={pick} onChange={(e) => setPick(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm">
            <option value="">— Seleziona —</option>
            {available.map((im) => <option key={im.id} value={im.id}>{etich(im)} · {im.titolo || im.codice || "Immobile"}{im.zona ? ` · ${im.zona}` : ""}</option>)}
          </select>
        </div>
        <button data-testid="link-existing-btn" onClick={add} disabled={!pick} className="px-3.5 py-2.5 rounded-lg bg-white border border-[var(--border)] text-sm font-semibold hover:border-[#FC82ED]/40 disabled:opacity-40 flex items-center gap-1.5"><Link2 size={15}/> Collega</button>
      </div>
      {!nuova && <NuovaNotiziaInline onCrea={setNuova} testid="nuova-notizia-inline" />}
      {onCreateAbbinato && <button data-testid="crea-incarico-abbinato-btn" onClick={onCreateAbbinato} className="mt-2 w-full px-3.5 py-2.5 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black text-sm font-semibold glow-pink flex items-center justify-center gap-1.5"><Plus size={15}/> Crea incarico abbinato al cliente</button>}
    </div>
  );
}

function LinkedImmobiliEdit({ propId, immobili, allImmobili, allNotizie, reload, onCreateAbbinato, nav }) {
  const [pick, setPick] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const available = opzioni(allImmobili, allNotizie, immobili, propId);
  const collega = async (body) => {
    try { const r = await api.post(`/proprietari/${propId}/collega`, body); toast.success("Collegamento salvato"); setPick(""); reload(r.data.immobili); }
    catch (e) { toast.error(apiError(e)); }
  };
  const collegaScelto = () => { const x = available.find((a) => a.id === pick); if (x) collega(x.tipo === "notizia" ? { notizia_id: x.id } : { immobile_id: x.id }); };
  const scollega = async (im) => {
    try { const r = await api.post(`/proprietari/${propId}/scollega`, im.tipo === "notizia" ? { notizia_id: im.id } : { immobile_id: im.id }); toast.success("Collegamento rimosso"); setConfirmId(null); reload(r.data.immobili); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-xl border p-3 border-[#FC82ED]/30 bg-[#FC82ED]/[0.05]">
      <div className="text-[11px] font-bold uppercase tracking-wide text-[#FC82ED] mb-2">Immobili collegati (facoltativo)</div>
      {immobili.length === 0 && (
        <div data-testid="edit-da-collegare" className="text-xs text-[var(--muted)] mb-2">Nessun incarico o notizia collegati.</div>
      )}
      <div className="flex flex-col gap-1.5 mb-2">
        {immobili.map((im) => (
          <div key={im.id} data-testid={`linked-imm-${im.id}`} className="flex items-center justify-between gap-2 bg-white border border-[var(--border)] rounded-lg px-3 py-2 text-sm">
            <button onClick={() => nav(im.tipo === "notizia" ? `/notizie/${im.id}` : `/immobili/${im.id}`)} className="flex items-center gap-2 truncate hover:text-[#FC82ED]"><Etichetta x={im} /> {im.titolo || im.codice || "Immobile"}{im.prezzo ? ` · ${euro(im.prezzo)}` : ""}</button>
            {confirmId === im.id ? (
              <span className="flex items-center gap-1.5">
                <button data-testid={`confirm-unlink-${im.id}`} onClick={() => scollega(im)} className="text-red-500 text-xs font-semibold">Conferma</button>
                <button onClick={() => setConfirmId(null)} className="text-slate-400 text-xs">Annulla</button>
              </span>
            ) : (
              <button data-testid={`unlink-imm-${im.id}`} onClick={() => setConfirmId(im.id)} className="text-slate-400 hover:text-red-500"><X size={15}/></button>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1">Collega incarico o notizia</label>
          <select data-testid="edit-link-existing-select" value={pick} onChange={(e) => setPick(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm">
            <option value="">— Seleziona —</option>
            {available.map((im) => <option key={im.id} value={im.id}>{etich(im)} · {im.titolo || im.codice || "Immobile"}{im.zona ? ` · ${im.zona}` : ""}</option>)}
          </select>
        </div>
        <button data-testid="edit-link-existing-btn" onClick={collegaScelto} disabled={!pick} className="px-3.5 py-2.5 rounded-lg bg-white border border-[var(--border)] text-sm font-semibold hover:border-[#FC82ED]/40 disabled:opacity-40 flex items-center gap-1.5"><Link2 size={15}/> Collega</button>
      </div>
      <NuovaNotiziaInline onCrea={(v) => collega({ nuova_notizia: v })} testid="edit-nuova-notizia-inline" />
      {onCreateAbbinato && <button data-testid="edit-crea-incarico-abbinato-btn" onClick={onCreateAbbinato} className="mt-2 w-full px-3.5 py-2.5 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black text-sm font-semibold glow-pink flex items-center justify-center gap-1.5"><Plus size={15}/> Crea incarico abbinato al cliente</button>}
    </div>
  );
}

function PropModal({ record, onClose, onSaved, nav }) {
  const { isSegreteria } = useAuth();
  const isEdit = !!record;
  const [f, setF] = useState({ tipo_cliente: "venditore" });
  const [immobili, setImmobili] = useState([]);        // edit: linked (server)
  const [selected, setSelected] = useState([]);        // create: local selection
  const [allImmobili, setAllImmobili] = useState([]);
  const [allNotizie, setAllNotizie] = useState([]);
  const [nuova, setNuova] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(!isEdit);
  const { dup, setDup, check } = useDupCheck("proprietario", record?.id);
  const { errors, clear, validate, applyServer, summary } = useFormErrors();
  const [tab, setTab] = useState(record?._scrollDocs ? "documenti" : record?._scrollDati || !isEdit ? "anagrafica" : "immobili");
  const set = (k) => (e) => { const v = e.target.value; setF((s) => ({ ...s, [k]: v })); clear(k); if (["telefono", "email", "codice_fiscale", "societa_piva"].includes(k)) check(k, v); };
  const setV = (k, v) => set(k)({ target: { value: v } });

  useEffect(() => {
    api.get("/immobili").then((r) => setAllImmobili(r.data)).catch(() => {});
    api.get("/notizie").then((r) => setAllNotizie((r.data || []).filter((n) => !n.immobile_id && !n._masked && n.stato !== "incarico_acquisito"))).catch(() => {});
    if (isEdit) {
      api.get(`/proprietari/${record.id}`).then((r) => { setF(r.data); setImmobili(r.data.immobili || []); setReady(true); }).catch((e) => { toast.error(apiError(e)); onClose(); });
    }
  }, []); // eslint-disable-line
  useEffect(() => {
    if (ready && record?._scrollDocs) setTimeout(() => document.querySelector('[data-testid="docs-section"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 200);
    if (ready && record?._scrollDati) setTimeout(() => { const el = document.querySelector('[data-testid="pr-cf"]'); el?.scrollIntoView({ behavior: "smooth", block: "center" }); el?.focus?.(); }, 250);
  }, [ready]); // eslint-disable-line

  const anagrafica = () => {
    const { id, immobili: _i, immobili_count, da_collegare, owner_agent_name, owner_agent_id, _masked, created_at, updated_at, created_by, created_by_name, origine, nome_display, agente_referente, ...rest } = f;
    return rest;
  };

  const createAbbinato = async () => {
    if (!f.nome) return toast.error("Inserisci almeno il nome del cliente");
    setSaving(true);
    try {
      const titolo = `Incarico ${f.nome || ""} ${f.cognome || ""}`.trim();
      const payload = isEdit
        ? { proprietario_id: record.id, titolo }
        : { crea_proprietario: anagrafica(), titolo };
      const { data } = await api.post("/immobili", payload);
      toast.success("Incarico creato e collegato al cliente. Completa la scheda tecnica.");
      onClose();
      nav(`/immobili/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const save = async () => {
    const errs = validate({ nome: [V.required], cognome: [V.required], codice_fiscale: [V.cf], email: [V.email], telefono: [V.phone], ...(isGiuridica(f) ? regoleGiuridica : {}) }, f);
    if (Object.keys(errs).length) { setTab("anagrafica"); return toast.error(errSummary(Object.keys(errs).length)); }
    setSaving(true); setDup(null);
    try {
      if (isEdit) {
        await api.put(`/proprietari/${record.id}`, anagrafica());
        toast.success("Cliente venditore aggiornato");
      } else {
        await api.post("/proprietari", { ...anagrafica(), immobili_ids: selected.filter((s) => s.tipo !== "notizia").map((s) => s.id), notizie_ids: selected.filter((s) => s.tipo === "notizia").map((s) => s.id), nuova_notizia: nuova || undefined });
        toast.success("Cliente venditore creato");
      }
      onSaved();
    } catch (e) { const dd = dupDetail(e); if (dd) { setDup(dd); return; } if (applyServer(e)) return; toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const nomeV = `${f.nome || ""} ${f.cognome || ""}`.trim() || "Venditore";
  const tabs = [["immobili", "Immobili collegati", isEdit ? immobili.length : selected.length + (nuova ? 1 : 0)], ["anagrafica", "Anagrafica"], ...(isEdit ? [["documenti", "Documenti e privacy"], ["storico", "Storico"]] : [])];
  return (
    <Modal title={isEdit ? "Scheda Cliente Venditore" : "Nuovo Cliente Venditore"} onClose={onClose} onSave={save} saving={saving} testid="proprietario-modal" ultimoId={record?.id} saveLabel={isEdit ? "Salva" : "Crea cliente"} summary={summary} scheda value={f} onRestore={setF}
      largo header={<div className="space-y-3"><SchedaHeader f={f} nuovo={!isEdit} titoloNuovo="Nuovo cliente venditore" /><SchedaTabs tabs={tabs} tab={tab} setTab={setTab} /></div>}
      footerLeft={isEdit ? <AzioniRapide cliente={{ type: "proprietario", id: record.id, label: nomeV }} /> : null}>
      <DuplicateWarning dup={dup} />
      {!ready ? <div className="skeleton h-64 rounded-xl" /> : (
        <>
          {tab === "anagrafica" && <div className="space-y-4">
          <Riquadro titolo="Tipo cliente"><TipoSoggetto value={f.tipo_soggetto} onChange={(v) => setV("tipo_soggetto", v)} prefix="pr" />
          {isGiuridica(f) && <SocietaFields f={f} set={setV} errors={errors} prefix="pr" />}</Riquadro>
          <Riquadro titolo={isGiuridica(f) ? "Legale rappresentante (chi firma)" : "Dati anagrafici"}>
          {isGiuridica(f) && <Field label="Qualifica (es. amministratore unico)" testid="pr-qualifica" value={f.qualita_di || ""} onChange={set("qualita_di")}/>}
          <Row><Field label="Nome" testid="pr-nome" required error={errors.nome} value={f.nome || ""} onChange={set("nome")}/><Field label="Cognome" testid="pr-cognome" required error={errors.cognome} value={f.cognome || ""} onChange={set("cognome")}/></Row>
          <Row><Field label="Codice Fiscale" testid="pr-cf" error={errors.codice_fiscale} value={f.codice_fiscale || ""} onChange={set("codice_fiscale")}/><Field label="Data di nascita" type="date" testid="pr-nascita" value={f.data_nascita || ""} onChange={set("data_nascita")}/></Row>
          <Field label="Luogo di nascita" testid="pr-luogo" value={f.luogo_nascita || ""} onChange={set("luogo_nascita")}/>
          </Riquadro>
          <Riquadro titolo="Recapiti">
          <Row><Field label="Telefono" testid="pr-tel" error={errors.telefono} value={f.telefono || ""} onChange={set("telefono")}/><Field label="Email" testid="pr-email" error={errors.email} value={f.email || ""} onChange={set("email")}/></Row>
          <AltriDati testid="pr-altri-recapiti"><Row><Field label="Telefono alternativo" testid="pr-tel2" value={f.telefono_alt || ""} onChange={set("telefono_alt")}/><Field label="PEC" testid="pr-pec" value={f.pec || ""} onChange={set("pec")}/></Row></AltriDati>
          </Riquadro>
          <Riquadro titolo="Residenza (serve per pubblicare)">
          <Row><Field label="Via/Piazza di residenza" testid="pr-indirizzo" value={f.indirizzo || ""} onChange={set("indirizzo")}/><Field label="Civico" testid="pr-res-civico" value={f.res_civico || ""} onChange={set("res_civico")}/></Row>
          <Row><Field label="Città" testid="pr-citta" value={f.citta || ""} onChange={set("citta")}/><Field label="CAP" testid="pr-cap" value={f.cap || ""} onChange={set("cap")}/></Row>
          <Field label="Provincia" testid="pr-provincia" value={f.provincia || ""} onChange={set("provincia")}/>
          </Riquadro>
          <Riquadro titolo="Informazioni e note">
          <Row>
            <Select label="Tipo cliente" testid="pr-tipo" value={f.tipo_cliente || "venditore"} onChange={set("tipo_cliente")} opts={[["venditore","Venditore"],["proprietario","Proprietario"],["venditore_proprietario","Venditore e Proprietario"]]} />
            <Field label="Referente / relazione" testid="pr-referente" value={f.referente || ""} onChange={set("referente")}/>
          </Row>
          <TextArea label="Note sul cliente" testid="pr-note" value={f.note || ""} onChange={set("note")}/>
          </Riquadro>
          </div>}

          {tab === "immobili" && (isEdit ? (
            <LinkedImmobiliEdit propId={record.id} immobili={immobili} allImmobili={allImmobili} allNotizie={allNotizie} reload={setImmobili} onCreateAbbinato={isSegreteria ? createAbbinato : null} nav={nav} />
          ) : (
            <LinkedImmobiliCreate allImmobili={allImmobili} allNotizie={allNotizie} selected={selected} setSelected={setSelected} nuova={nuova} setNuova={setNuova} onCreateAbbinato={isSegreteria ? createAbbinato : null} />
          ))}

          {tab === "documenti" && isEdit && (<div className="space-y-4">
            <Riquadro titolo="Documenti del cliente"><EntityDocs entityType="proprietario" entityId={record.id} /></Riquadro>
            <Riquadro titolo="Privacy e consensi"><GdprContactPanel contactType="proprietario" contactId={record.id} /></Riquadro>
          </div>)}
          {tab === "storico" && isEdit && <Riquadro titolo="Appuntamenti e storico">
              <AppuntamentiScheda entity={{ cliente_id: record.id }}
                prefill={{ client: { type: "proprietario", id: record.id, label: nomeV }, titolo: `Appuntamento ${f.nome || ""}`.trim() }} />
              <p className="text-xs text-[var(--muted)]">Creato il {f.created_at ? new Date(f.created_at).toLocaleDateString("it-IT") : "—"}{f.origine === "notizia" ? " da una notizia" : ""}</p>
          </Riquadro>}
        </>
      )}
    </Modal>
  );
}

function ciBadge(scad) {
  if (!scad) return null;
  const d = Math.ceil((new Date(scad) - new Date()) / 86400000);
  if (d < 0) return <span data-testid="ci-badge-scaduto" className="px-2 py-0.5 rounded-full text-[10px] font-bold border text-red-600 bg-red-500/10 border-red-500/30">Scaduto da {Math.abs(d)}gg</span>;
  if (d <= 60) return <span data-testid="ci-badge-scadenza" className="px-2 py-0.5 rounded-full text-[10px] font-bold border text-[#FFB020] bg-[#FFB020]/10 border-[#FFB020]/30">In scadenza ({d}gg)</span>;
  return <span data-testid="ci-badge-valido" className="px-2 py-0.5 rounded-full text-[10px] font-bold border text-emerald-600 bg-emerald-400/10 border-emerald-400/30">Valido</span>;
}

function DocsSection({ propId }) {
  const [files, setFiles] = useState([]);
  const load = () => api.get(`/files?entity_type=proprietario&entity_id=${propId}&kind=documento`).then((r) => setFiles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line
  return (
    <div className="rounded-xl border border-[var(--border)] p-3 space-y-3" data-testid="docs-section">
      <DocSlot propId={propId} tipo="documento_identita" label="Documento d'identità" files={files} onDone={load} withScadenza />
      <DocSlot propId={propId} tipo="codice_fiscale" label="Codice Fiscale" files={files} onDone={load} />
      <DocSlot propId={propId} tipo="privacy" label="Privacy (firmata)" files={files} onDone={load} />
    </div>
  );
}

function DocSlot({ propId, tipo, label, files, onDone, withScadenza }) {
  const [busy, setBusy] = useState(false);
  const current = files.filter((f) => f.doc_tipo === tipo);
  const up = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("entity_type", "proprietario"); fd.append("entity_id", propId);
      fd.append("kind", "documento"); fd.append("doc_tipo", tipo);
      await postUpload(fd);
      toast.success(`${label} caricato`); onDone();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  const dl = async (f) => {
    try { const r = await api.get(`/files/${f.id}/download`, { responseType: "blob" }); const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = f.original_filename || label; a.click(); URL.revokeObjectURL(url); }
    catch (err) { toast.error(apiError(err)); }
  };
  const setScad = async (f, val) => {
    try { await api.patch(`/files/${f.id}`, { scadenza: val }); toast.success("Scadenza aggiornata"); onDone(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-sm font-medium flex items-center gap-2"><FileBadge size={14} className="text-[#FC82ED]"/> {label}</span>
        <label data-testid={`upload-${tipo}`} className="px-3 py-1.5 rounded-lg bg-white border border-[var(--border)] text-xs font-semibold hover:border-[#FC82ED]/40 cursor-pointer">
          {busy ? "Caricamento…" : (current.length ? "Sostituisci" : "Carica")}
          <input type="file" className="hidden" onChange={up} />
        </label>
      </div>
      {current.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {current.map((f) => (
            <div key={f.id} className="flex flex-col items-center gap-1 w-16">
              <DocThumb file={f} onOpen={dl} />
              <button data-testid={`doc-${f.id}`} onClick={() => dl(f)} className="text-[10px] text-slate-500 hover:text-[#FC82ED] truncate w-full text-center" title={f.original_filename}>{f.original_filename}</button>
            </div>
          ))}
        </div>
      )}
      {withScadenza && current.length > 0 && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <label className="text-[11px] text-[var(--muted)]">Scadenza documento</label>
          <input data-testid={`scad-${tipo}`} type="date" value={current[0].scadenza || ""} onChange={(e) => setScad(current[0], e.target.value)}
                 className="px-2 py-1.5 rounded-lg bg-white border border-[var(--border)] text-xs outline-none focus:border-[#FC82ED]" />
          {ciBadge(current[0].scadenza)}
        </div>
      )}
    </div>
  );
}

function DocThumb({ file, onOpen }) {
  const [url, setUrl] = useState(null);
  const isImg = /\.(jpg|jpeg|png|webp|heic|heif|gif)$/i.test(file.original_filename || "") || (file.content_type || "").startsWith("image/");
  useEffect(() => {
    let active = true; let obj;
    if (isImg) api.get(`/files/${file.id}/download`, { responseType: "blob" }).then((r) => { if (!active) return; obj = URL.createObjectURL(r.data); setUrl(obj); }).catch(() => {});
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [file.id]); // eslint-disable-line
  return (
    <button data-testid={`doc-thumb-${file.id}`} onClick={() => onOpen(file)} title={file.original_filename}
            className="w-16 h-16 rounded-lg border border-[var(--border)] overflow-hidden bg-[#F7F7F8] flex items-center justify-center shrink-0 hover:border-[#FC82ED]/60 transition-colors">
      {isImg && url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <FileText size={22} className="text-[#FC82ED]" />}
    </button>
  );
}
